import React, { useContext, useMemo } from 'react';
import cn from '@/utils/cn';
import DndAnswerContext from 'components/common/Context';
import { useDrop } from 'react-dnd';
import I18n from 'i18n-js';
import useSlideShowStore from '@/components/admin/SlideShow/stores/useSlideShowStore';
import styles from './DraggableChoicesBank.module.scss';
import DraggableChoice from '../DraggableChoice';
import QuestionBody from './QuestionBody';
import AnswerKeyButton from './AnswerKeyButton';
import ItemTypes from '../../ItemTypes';
import { DraggableItemType } from '../../types';
import SubmissionTextHelper from '../../../../SubmissionTextHelper';
import { sortedDraggableChoicesByPosition } from '../../Builder/DraggableChoiceEditor/Utils';
import { isVocabQuestion } from '../../utils';
import ClearButton from './ClearButton';

interface DraggableChoicesBankProps {
  correctChoices: { [key: string]: string[] };
  currentChoices: { [key: string]: string[] };
  isHorizontallyPositioned?: boolean;
  isComplete?: boolean;
  isSubmittable?: boolean;
  isSubmitDisabled: boolean;
  onSubmit: (isSubmitting: boolean) => any;
  savedChoices: { [key: string]: string[] };
  shouldDisplayPrompt?: boolean;
  submissionTextHelper: SubmissionTextHelper;
  useClearButton?: boolean;
}

const DraggableChoicesBank = ({
  correctChoices = {},
  currentChoices = {},
  isComplete = false,
  isHorizontallyPositioned,
  isSubmittable = false,
  isSubmitDisabled,
  onSubmit,
  savedChoices = {},
  shouldDisplayPrompt = true,
  submissionTextHelper,
  useClearButton = false,
}: DraggableChoicesBankProps) => {
  const {
    answerChoiceDispatch,
    draggableChoices,
    answerChoices,
    showAnswerKey,
    useFloatingSubmitBtn,
    studentId,
  } = useContext(DndAnswerContext);

  const isTeacher = useSlideShowStore(state => state.userType) === 'Teacher';
  const isProfessionalDevelopment = useSlideShowStore(
    state => state.slideShow?.activityType === 'professional_development',
  );

  const sortedChoices = useMemo(() => (
    sortedDraggableChoicesByPosition(draggableChoices).map(([key]) => key)
  ), [answerChoices]);

  const remainingChoices = useMemo(() => (
    Object.keys(draggableChoices)
      .filter(key => !Object.values(answerChoices).flat().includes(key))
  ), [answerChoices]);

  const [{ highlighted }, drop] = useDrop(() => ({
    accept: ItemTypes.draggableChoice,
    collect: (monitor) => {
      const item: DraggableItemType = monitor.getItem();
      return {
        canDrop: monitor.canDrop() && item && !remainingChoices.includes(item.id),
        highlighted: monitor.isOver() && item && !remainingChoices.includes(item.id),
        isOver: monitor.isOver(),
      };
    },
    drop: (item: DraggableItemType) => {
      answerChoiceDispatch({ payload: { draggableChoiceId: item.id }, type: 'REMOVE' });
    },
  }), [answerChoices]);

  const handleSubmit = () => onSubmit(true);

  const renderAnswerKey = () => {
    // The order of these statements is important
    if (showAnswerKey) return false;
    if (isComplete) return true;
    if (isSubmittable && studentId) return false;

    return !(isProfessionalDevelopment && isTeacher);
  };

  return (
    <div
      className={cn(styles.bankContainer,
        {
          [styles.horizontalBankContainer]: isHorizontallyPositioned,
          [styles.roundedBorder]: isVocabQuestion(draggableChoices),
        })}
    >
      <div className={styles.questionInfo}>
        {shouldDisplayPrompt && <QuestionBody ttsEnabled />}
        {renderAnswerKey() && <AnswerKeyButton />}
      </div>

      <div
        ref={drop}
        className={cn(styles.draggableChoicesContainer, {
          [styles.highlighted]: highlighted,
          [styles.horizontalDraggableChoicesContainer]: isHorizontallyPositioned,
        })}
      >
        {!showAnswerKey && sortedChoices.map(key => (
          <DraggableChoice
            key={key}
            hideDelete={Object.keys(draggableChoices).length === 1}
            id={key}
            isHorizontallyPositioned={isHorizontallyPositioned}
            {...draggableChoices[key]}
            isRemainingChoice={remainingChoices.includes(key)}
          />
        ))}
      </div>
      <div
        className={cn(styles.submissionContainerWrapper, {
          'tw-my-1.5': isHorizontallyPositioned,
          'tw-pb-6': isVocabQuestion(draggableChoices),
        })}
      >
        {isSubmittable && !useFloatingSubmitBtn && (
          <div className={styles.submissionContainer}>
            {submissionTextHelper.hasMessage() && submissionTextHelper.submissionAlert()}
            <div className={styles.buttonRow}>
              <button
                className={cn('btn btn--purple', { 'btn--sm': isHorizontallyPositioned })}
                disabled={isSubmitDisabled}
                type="button"
                onClick={handleSubmit}
              >
                {I18n.t('submit')}
              </button>
              {useClearButton && (
                <ClearButton
                  correctChoices={correctChoices}
                  currentChoices={currentChoices}
                  isComplete={isComplete}
                  savedChoices={savedChoices}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DraggableChoicesBank;
